/**
 * Konkord: Chromium on Windows encodes H.264 Constrained Baseline (42e01f),
 * the profile LiveKit settles on, with OpenH264 on the processor, while
 * Baseline, Main and High run on the GPU / integrated graphics (measured on an
 * RTX 3070: OpenH264 vs NVIDIA Media Foundation encoder). When the browser
 * reports exactly that, our offers leave the 42xxxx profiles out so the voice
 * server picks High, which the GPU encodes and every browser decodes.
 */

type H264Support = {
  /** Some H.264 profile is encoded in hardware */
  hardware: boolean;
  /** Offers should leave out the Baseline profiles */
  dropBaseline: boolean;
};

let support: H264Support | undefined;
let installed = false;

/** Konkord: connections whose offers keep every H.264 profile */
const keepAll = new WeakSet<RTCPeerConnection>();

/**
 * Konkord: this connection's offers keep the Baseline profiles. A direct
 * copy fed by the shared encode carries its Constrained Baseline stream (the
 * connection itself only encodes a tiny stand-in), and Safari, the only
 * engine on the iPhone, takes Constrained Baseline but none of the Main /
 * High profiles Chromium offers without it: an iPhone answered without the
 * video and never connected (31/10)
 */
export function keepAllH264(pc: RTCPeerConnection) {
  keepAll.add(pc);
}

async function powerEfficient(profile: string) {
  try {
    const info = await navigator.mediaCapabilities.encodingInfo({
      type: "webrtc",
      video: {
        contentType: `video/H264;profile-level-id=${profile};packetization-mode=1`,
        width: 1920,
        height: 1080,
        bitrate: 4_500_000,
        framerate: 30,
      },
    });
    return info.supported && info.powerEfficient;
  } catch {
    return undefined;
  }
}

async function detect(): Promise<H264Support> {
  const [constrained, baseline, main, high] = await Promise.all(
    ["42e01f", "42001f", "4d001f", "640032"].map(powerEfficient),
  );
  return {
    hardware: !!(constrained || baseline || main || high),
    dropBaseline: constrained === false && !!(high || main),
  };
}

/**
 * Konkord: whether this computer encodes H.264 in hardware (undefined while unknown)
 */
export function h264Hardware() {
  return support?.hardware;
}

/**
 * Konkord: remove the H.264 Baseline / Constrained Baseline payloads (and their
 * retransmission payloads) from every video section, unless nothing else
 * of H.264 would be left
 */
export function stripBaselineH264(sdp: string) {
  const eol = sdp.includes("\r\n") ? "\r\n" : "\n";
  return sdp
    .split(/(?=^m=)/m)
    .map((section) => {
      // only what we send: receiving keeps every profile, so streams from
      // computers that don't do this still play
      if (!section.startsWith("m=video") || !/^a=sendonly/m.test(section))
        return section;

      const h264 = [...section.matchAll(/^a=rtpmap:(\d+) H264\/90000/gim)].map(
        (match) => match[1],
      );
      const baseline = h264.filter((pt) =>
        /profile-level-id=42/i.test(
          section.match(new RegExp(`^a=fmtp:${pt} (.*)$`, "m"))?.[1] ?? "",
        ),
      );
      if (!baseline.length || baseline.length === h264.length) return section;

      const drop = new Set(baseline);
      for (const match of section.matchAll(/^a=fmtp:(\d+) apt=(\d+)/gm)) {
        if (drop.has(match[2])) drop.add(match[1]);
      }

      const lines = section.split(eol).filter((line) => {
        const match = line.match(/^a=(?:rtpmap|fmtp|rtcp-fb):(\d+)/);
        return !(match && drop.has(match[1]));
      });
      lines[0] = lines[0]
        .split(" ")
        .filter((part, index) => index < 3 || !drop.has(part))
        .join(" ");
      return lines.join(eol);
    })
    .join("");
}

/**
 * Konkord: check the encoders once and, when it helps, adjust our offers.
 * LiveKit sends the same offer object it sets locally, so changing it here
 * keeps both sides in agreement.
 */
export function installH264Hardware() {
  if (installed || typeof RTCPeerConnection === "undefined") return;
  installed = true;
  void detect().then((result) => (support = result));

  const original = RTCPeerConnection.prototype.setLocalDescription as (
    description?: RTCLocalSessionDescriptionInit,
  ) => Promise<void>;
  RTCPeerConnection.prototype.setLocalDescription = function (
    this: RTCPeerConnection,
    description?: RTCLocalSessionDescriptionInit,
  ) {
    if (
      support?.dropBaseline &&
      !keepAll.has(this) &&
      description?.type === "offer" &&
      description.sdp
    ) {
      try {
        description.sdp = stripBaselineH264(description.sdp);
      } catch {
        // read-only description: the offer stays as the browser made it
      }
    }
    return original.call(this, description);
  } as typeof RTCPeerConnection.prototype.setLocalDescription;
}

/**
 * Konkord: Firefox encodes H.264 with OpenH264 on the processor and, measured
 * with stream-e2e --phase browsers, sent the voice server only 320x180 at
 * 0.2 Mbps (16 FPS on the direct copy) while VP8 went out at 1280x720,
 * 29 FPS, 4.7 Mbps: Firefox streams in VP8, the others in H.264
 */
export function prefersH264Encoding() {
  return !/firefox/i.test(navigator.userAgent);
}
